import React, { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { FilterHorizontalScroll } from '@ui/v2/TableFilter/FilterHorizontalScroll'

import { AnalyticsAssetType, AnalyticsPeriodType } from '@model/AnalyticsDto'

import styles from './FiltersRow.module.scss'

export const initialFiltersQuery = () => {
  return { assetType: null, periodType: null }
}

interface FiltersRowComponentProps {
  filters: AnalyticsFilters
  onFiltersChange: (filters: AnalyticsFilters) => void
}

export interface AnalyticsFilters {
  assetType: AnalyticsAssetType | null
  periodType: AnalyticsPeriodType | null
}

const FiltersRowComponent: React.FC<FiltersRowComponentProps> = (props) => {
  const { filters, onFiltersChange } = props

  const { t } = useTranslation()

  const handleAssetsFiltersChange = useCallback(
    (assetType: AnalyticsAssetType | null) => {
      onFiltersChange({
        ...filters,
        assetType,
      })
    },
    [filters, onFiltersChange]
  )

  const handlePeriodFiltersChange = useCallback(
    (periodType: AnalyticsPeriodType | null) => {
      onFiltersChange({
        ...filters,
        periodType,
      })
    },
    [filters, onFiltersChange]
  )

  const handleClearFilters = useCallback(() => {
    onFiltersChange(initialFiltersQuery())
  }, [onFiltersChange])

  return (
    <div className={styles.filtersLeft}>
      <FilterHorizontalScroll>
        <AssetsFilter filter={filters.assetType} onChange={handleAssetsFiltersChange} />
        <PeriodFilter filter={filters.periodType} onChange={handlePeriodFiltersChange} />
      </FilterHorizontalScroll>

      <div className={styles.clearButton}>
        <Button.Secondary size='md' onClick={handleClearFilters}>
          {t('Clear Filters')}
        </Button.Secondary>
      </div>
    </div>
  )
}

interface AssetsFilterProps {
  filter: AnalyticsAssetType | null
  onChange: (option: AnalyticsAssetType | null) => void
}

const AssetsFilter: React.FC<AssetsFilterProps> = (props) => {
  const { filter, onChange } = props

  const { t } = useTranslation()

  const options = [
    { id: AnalyticsAssetType.Forex, name: t('Analytics.Forex') },
    { id: AnalyticsAssetType.Commodities, name: t('Analytics.Commodities') },
    { id: AnalyticsAssetType.Crypto, name: t('Analytics.Crypto') },
    { id: AnalyticsAssetType.Indices, name: t('Analytics.Indices') },
  ]

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleChange = useCallback(
    (option: AnalyticsAssetType) => {
      onChange(option)
      setIsDropdownOpen(false)
    },
    [onChange]
  )

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation()
      event.preventDefault()
      onChange(null)
      setIsDropdownOpen(false)
    },
    [onChange]
  )

  return (
    <FilterHorizontalScroll>
      <Popover
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        content={
          <PopoverCard>
            <PopoverList>
              {options.map((x) => {
                const isActive = filter === x.id
                return (
                  <ListItem
                    key={x.id}
                    active={isActive}
                    endSlot={
                      isActive
                        ? {
                            icon: { icon: { icon: CircleCheckIcon, color: 'text-purple-high-em' } },
                          }
                        : undefined
                    }
                    onClick={() => handleChange(x.id)}
                  >
                    {x.name}
                  </ListItem>
                )
              })}
            </PopoverList>
          </PopoverCard>
        }
        label={t('Analytics.Assets')}
      >
        <Button.Secondary
          size='md'
          iconRight={ChevronIcon.Down}
          endDecorator={
            filter && (
              <Chip selected size='xs' onDelete={handleClear}>
                {'1'}
              </Chip>
            )
          }
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {t('Analytics.Assets')}
        </Button.Secondary>
      </Popover>
    </FilterHorizontalScroll>
  )
}

interface PeriodFilterProps {
  filter: AnalyticsPeriodType | null
  onChange: (option: AnalyticsPeriodType | null) => void
}

const PeriodFilter: React.FC<PeriodFilterProps> = (props) => {
  const { filter, onChange } = props

  const { t } = useTranslation()

  const options = [
    { id: AnalyticsPeriodType.DAY, name: t('Analytics.Daily') },
    { id: AnalyticsPeriodType.WEEK, name: t('Analytics.Weekly') },
    { id: AnalyticsPeriodType.MONTH, name: t('Analytics.Monthly') },
  ]

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleChange = useCallback(
    (option: AnalyticsPeriodType) => {
      onChange(option)
      setIsDropdownOpen(false)
    },
    [onChange]
  )

  const handleClear = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation()
      event.preventDefault()
      onChange(null)
      setIsDropdownOpen(false)
    },
    [onChange]
  )

  return (
    <FilterHorizontalScroll>
      <Popover
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        label={t('Analytics.Period')}
        content={
          <PopoverCard>
            <PopoverList>
              {options.map((x) => {
                const isActive = filter === x.id
                return (
                  <ListItem
                    key={x.id}
                    active={isActive}
                    endSlot={
                      isActive
                        ? {
                            icon: { icon: { icon: CircleCheckIcon, color: 'text-purple-high-em' } },
                          }
                        : undefined
                    }
                    onClick={() => handleChange(x.id)}
                  >
                    {x.name}
                  </ListItem>
                )
              })}
            </PopoverList>
          </PopoverCard>
        }
      >
        <Button.Secondary
          size='md'
          iconRight={ChevronIcon.Down}
          endDecorator={
            filter && (
              <Chip selected size='xs' onDelete={handleClear}>
                {'1'}
              </Chip>
            )
          }
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {t('Analytics.Period')}
        </Button.Secondary>
      </Popover>
    </FilterHorizontalScroll>
  )
}

export const FiltersRow = memo(FiltersRowComponent)
